/* eslint-disable @typescript-eslint/no-unused-vars */
import { Box, IconButton } from '@mui/material'
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import SVG from 'react-inlinesvg'
import PatternToolBar from './ToolBar'
import {
  StartRenderer,
  SetCurrentItem,
  RestorePattern,
  PerformOperation as Perform3DTask,
  APPLICATION_MODES,
  SetCanvasMode,
  SetAccentScale,
  APPLYTOALL_ACTIVE,
  getApplyToAllActive,
  setApplyToAllActive,
  getApplicationMode,
  ResetTemplate,
  SetAccentNumber,
  SetAccentRotation,
  SetCameraHorizontalPosition,
  CameraZoomInPressed,
  CameraZoomOutPressed,
  SetFullScreen,
  getTopicType,
  CheckCurrentMode,
  SetPrintTiling,
} from './renderer/main'
import { Stage, Layer, Text, Transformer } from 'react-konva'
import { KonvaEventObject } from 'konva/lib/Node'
import { DoneOutline, EditOutlined, PreviewOutlined } from '@mui/icons-material'
import { IPatternAssets, PatternDraft, PatternProps, PatternSelections } from '@/types'
import { SettingButtonWrapper } from './__styled'
import colors from '@/styles/colors'
import { SVGS } from '@/assets/svgs'
import { useBrowserQuery } from '@/hooks/common'
import { useLocation, useNavigate } from 'react-router-dom'
import { useConfirm } from '@/hooks/common'

const a = document.createElement('a')
a.style.display = 'none'

interface Props {
  assets: IPatternAssets
  selection: PatternSelections
  selectedTab: string
  patternData: PatternDraft
  textData: string
  isEditMode: boolean
  isPagePreview: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onEdit: (data: Omit<PatternDraft, 'preview'>) => void
  addScreenshot: (file: File) => void
  onPatternRestore: (patternProps: PatternProps) => void
  onAssetsUpdate: (assets: IPatternAssets) => void
  onToggleFullScreenMode: () => void
  toggleSliderVisibilityCallBack: (visible: boolean) => void
  updateSliderFromAccentScaleCallBack: (value: number) => void
  updateSliderFromAccentNumberCallBack: (value: number) => void
  updateSliderFromAccentRotationCallBack: (value: number) => void
  updateSliderFromPrintTilingCallBack: (value: number) => void
  updateSliderFromCameraHorizontalCallback: (value: number) => void
  onResetTemplate: () => void
}

const PatternPreview = forwardRef(function PatternPreview(
  {
    assets,
    selection,
    selectedTab,
    patternData,
    textData,
    isEditMode,
    isPagePreview,
    onEdit,
    addScreenshot,
    onPatternRestore,
    onAssetsUpdate,
    onToggleFullScreenMode,
    toggleSliderVisibilityCallBack,
    updateSliderFromAccentScaleCallBack,
    updateSliderFromAccentNumberCallBack,
    updateSliderFromAccentRotationCallBack,
    updateSliderFromPrintTilingCallBack,
    updateSliderFromCameraHorizontalCallback,
    onResetTemplate,
  }: Props,
  ref,
) {
  const [patternLoaded, setPatternLoaded] = useState(false)
  const [textLoaded, setTextLoaded] = useState(false)
  const [altKeyPressed, setAltKeyPressed] = useState(false)
  const [showOverlay, setShowOverlay] = useState(true)
  const [isInspectorShown, setIsInspectorShown] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [textElements, setTextElements] = useState<any[]>([])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [konvaElements, setKonvaElements] = useState<any[]>([])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [currentEditingElement, setCurrentEditingElement] = useState<any>(null)
  const [appMode, setAppMode] = useState(APPLICATION_MODES.FULL_SCREEN)
  const [currentTab, setCurrentTab] = useState('template')
  const [isSliderVisible, setSliderVisibility] = useState<boolean>(false)
  const [applyAll, setApplyAll] = useState(false)

  const canvas = useRef(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const textContainer = useRef<any>()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stage = useRef<any>()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const transformer = useRef<any>()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const textArea = useRef<any>()
  const konvaImageData = useRef(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const toolbarRef = useRef<any>(null)

  const query = useBrowserQuery()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  // const mode = query.get('mode') || 'preview'
  const mode = isPagePreview ? 'pagePreview' : isEditMode ? 'edit' : 'preview'

  useEffect(() => {
    if (canvas.current) {
      StartRenderer({
        canvas: canvas.current,
        assets: assets,
        onEdit: onEdit,
        canGrab: true,
        mode,
        onPatternRestore: onPatternRestore,
        onAssetsUpdate: onAssetsUpdate,
        setMode: (m: number) => {
          setMode(m)
        },
        toggleSliderVisibility: toggleSliderVisibility,
        updateSliderFromAccentScale: updateSliderFromAccentScale,
        updateSliderFromAccentNumber: updateSliderFromAccentNumber,
        updateSliderFromAccentRotation: updateSliderFromAccentRotation,
        updateSliderFromPrintTiling: updateSliderFromPrintTiling,
        updateSliderFromCameraHorizontalPosition: updateSliderFromCameraHorizontalPosition,
      })
      setAppMode(APPLICATION_MODES.FULL_SCREEN)
      const canvasObj = canvas.current as HTMLCanvasElement
      canvasObj.addEventListener('keydown', onKeyDown)
      canvasObj.addEventListener('keyup', onKeyUp)
    }
  }, [canvas])

  const handleUpdateOverlay = (status: boolean) => {
    setShowOverlay(status)
    Perform3DTask('toggleOverlay', status)
  }

  useEffect(() => {
    if (mode === 'preview') {
      SetCurrentItem(null, '')

      // if(appMode === APPLICATION_MODES.FULL_SCREEN)
      // {
      //   SetCanvasMode('fullScreen')
      //   return;
      // }
    }

    // console.log('====SET CANVAS MODE FROM PREVIEW: ', mode, ' APPLICATION MODE: ', appMode )

    handleUpdateOverlay(mode == 'edit')
    SetCanvasMode(mode)
  }, [mode])

  useEffect(() => {
    if (patternData && !patternLoaded) {
      RestorePattern(patternData, true, function () {
        setShowOverlay(true)
        setPatternLoaded(true)
      })
    }
  }, [patternData])

  useEffect(() => {
    if (selection.color && selectedTab === 'color' && currentEditingElement) {
      currentEditingElement.attrs.fill = selection.color
      transformer.current.nodes([currentEditingElement])
    }
  }, [selection, selectedTab, currentEditingElement])

  useEffect(() => {
    if (!textContainer.current) return
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
    function onTextElementChanged(e: any) {
      onTextUpdated()
    }

    const canvasWidth = textContainer.current.parentElement.clientWidth
    const canvasHeight = textContainer.current.parentElement.clientHeight
    let index = 0
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const temp: any[] = []
    textElements.forEach((elData) => {
      const elProps = Object.assign({}, elData)
      delete elProps.type
      elProps.x += canvasWidth / 2
      elProps.y += canvasHeight / 2
      elProps.draggable = true
      elProps.key = index++
      elProps.onDragEnd = onTextElementChanged
      temp.push(React.createElement(Text, elProps))
    })
    setKonvaElements(temp)
  }, [textContainer, textElements])

  useEffect(() => {
    if (textData && !textLoaded) {
      setTextElements(JSON.parse(textData) || [])
      setTextLoaded(true)
    }
  }, [textData])

  useEffect(() => {
    CheckCurrentMode(appMode)
  }, [appMode])

  useImperativeHandle(ref, () => {
    return {
      updateRender() {
        updateRendererState()
      },

      getScreenShotFile(callback: (file: File) => void) {
        return getScreenShotFile(callback)
      },

      saveScreenShot() {
        onClickDone()
      },
      addText(type: number) {
        addElementByType(type)
      },
      setMode(mode: number) {
        setMode(mode)
      },
      onSliderAccentScaleChangeRef(value: number) {
        setAccentSlider(value)
      },
      onSliderAccentNumberChangeRef(value: number) {
        SetAccentNumber(value)
      },

      onSliderAccentRotationChangeRef(value: number) {
        SetAccentRotation(value)
      },

      onSliderPrintRotationChangeRef(value: number) {
        SetPrintTiling(value)
      },

      onCameraHorizontalPositionChangeRef(value: number) {
        SetCameraHorizontalPosition(value)
      },
      onZoomInRef() {
        CameraZoomInPressed()
      },

      onZoomOutRef() {
        CameraZoomOutPressed()
      },

      updateOverlayStatus(status: boolean) {
        // setShowOverlay(status)
        // Perform3DTask('toggleOverlay', status)
        handleUpdateOverlay(status)
      },

      changeTemplate(accentID: any) {
        ResetTemplate(accentID)
        onResetTemplate()
      },
    }
  }, [selection, selectedTab, currentTab, textElements])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onKeyDown = (event: any) => {
    setAltKeyPressed(event.altKey)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onKeyUp = (event: any) => {
    setAltKeyPressed(event.altKey)
  }

  const updateRendererState = () => {
    setAppMode(APPLICATION_MODES.NONE)
    if (currentTab === 'text' && selectedTab !== 'text') {
      Perform3DTask('applyText', konvaImageData.current)
    }
    SetCurrentItem(selection, selectedTab)
    setCurrentTab(selectedTab)
    updateCanvasData()
  }

  const updateCanvasData = () => {
    window.requestAnimationFrame(function () {
      if (selectedTab === 'text') konvaImageData.current = stage.current.toDataURL()
    })
  }

  const toggleOverlay = () => {
    setShowOverlay(!showOverlay)
    Perform3DTask('toggleOverlay', !showOverlay)
  }

  const toggleInspector = () => {
    setIsInspectorShown(!isInspectorShown)
    Perform3DTask('toggleInspector', !isInspectorShown)
  }

  /**
   * Will make browser prompt to download file.
   * Can be used to download in memory image as file etc.
   * @memberof HELPERS
   * @param {(String|Blob)} data string data of the file
   * @param {String} fileName the filename of the browser downloaded file
   * @param {String} [type] the file mime type
   * @param {Boolean} [wantblob=false] Flag to return a blob Object instead of prompting user to
   * download
   * @returns {?Blob}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const downloadData = (data: any, fileName: string, type: string, wantblob: boolean) => {
    let blob
    if (type) {
      if (type !== 'blob') {
        if (type === 'json') {
          blob = createBlob(JSON.stringify(data))
        } else {
          blob = createBlob(data)
        }
      } else {
        blob = data
      }
    } else {
      if (typeof data == 'string') {
        if (data.match(/^data:image\/[^;]/)) {
          // eslint-disable-next-line camelcase
          const image_data = atob(data.split(',')[1])
          // Use typed arrays to convert the binary data to a Blob
          // eslint-disable-next-line camelcase
          const arraybuffer = new ArrayBuffer(image_data.length)
          const view = new Uint8Array(arraybuffer)
          // eslint-disable-next-line camelcase
          for (let i = 0; i < image_data.length; i++) {
            // eslint-disable-next-line camelcase
            view[i] = image_data.charCodeAt(i) & 0xff
          }
          blob = createBlob(arraybuffer, 'image/png')
        } else {
          blob = createBlob(data, 'image/png')
        }
      } else if (data instanceof Blob) {
        blob = /** @type Blob */ data
      } else {
        blob = createBlob(JSON.stringify(data))
      }
    }

    if (wantblob) {
      return /** @type Blob */ blob
    } else {
      const url = window.URL.createObjectURL(blob)
      document.body.appendChild(a)
      a.href = url
      a.download = fileName
      a.click()
      window.URL.revokeObjectURL(url)
    }
  }

  /**
   * Creates a blob from passed string of data
   * @param {String|Blob|ArrayBuffer|ArrayBufferView} string String data
   * @param {String} [type="octec/stream"] - type of blob to form. Can only be one of the acceptable
   * blob types
   * @returns {Blob} Blob
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const createBlob = (name: any, type?: string) => {
    return new Blob([name], {
      type: type || 'octet/stream',
    })
  }

  const onTogglePreview = () => {
    query.set('mode', mode === 'edit' ? 'preview' : 'edit')
    navigate(`${pathname}?${query.toString()}`, { replace: true })
  }

  const onClickPrivacy = () => {}

  const onClickDone = async () => {
    // addScreenshot
    const file = await takeScreenShotPromise()
    addScreenshot(file)
  }

  const getScreenShotFile = async (callback: (file: File) => void) => {
    const file = await takeScreenShotPromise()
    callback(file)
  }

  const takeScreenShotPromise = () => {
    return new Promise<File>((resolve, reject) => {
      Perform3DTask(
        'screenshot',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (data: any) => {
          // console.log(
          //     `%c [SCREENSHOT DEBUG] Raw Data Received: `,
          //     'background: #333; color: yellow; font-weight: bold;',
          //     {
          //       type: typeof data,
          //       isNull: data === null,
          //       isUndefined: data === undefined,
          //       length: data?.length || 0,
          //       preview: typeof data === 'string' ? data.substring(0, 50) + '...' : data
          //     }
          // );
          //
          // if (!data) {
          //   console.error('%c [SCREENSHOT ERROR] Data is Null or Undefined!', 'background: red; color: white; font-size: 14px;');
          //   reject(new Error("Screenshot failed: No data received from 3D Engine"));
          //   return;
          // }
          //
          // if (typeof data === 'string' && data.length < 100) {
          //   console.warn('%c [SCREENSHOT WARNING] Data string seems too short to be a valid image!', 'background: orange; color: black;');
          // }
          //
          // // 3. Render in Console (if valid string)
          // if (typeof data === 'string' && data.startsWith('data:image')) {
          //   console.log(
          //       `%c [SCREENSHOT SUCCESS] Rendering Image in Console... `,
          //       'background: #27ae60; color: white; font-weight: bold; padding: 4px;'
          //   );
          //
          //   console.log(
          //       '%c ',
          //       `font-size: 1px;
          //  padding: 150px;
          //  background-image: url(${data});
          //  background-size: contain;
          //  background-repeat: no-repeat;
          //  color: transparent;`
          //   );
          // } else {
          //   console.log('%c [SCREENSHOT INFO] Data is not a base64 string (Cannot render console preview)', 'color: grey');
          // }

          const imageBlob = downloadData(data, 'screenshot.png', '', true)
          const file = new File([imageBlob], 'screenshot.png', {
            type: 'image/png',
          })
          resolve(file)
        },
        512,
      )
    })
  }

  const handleUndo = () => {
    setAppMode(APPLICATION_MODES.NONE)
    Perform3DTask('undoPatternEdit')
  }

  const handleRedo = () => {
    setAppMode(APPLICATION_MODES.NONE)
    Perform3DTask('redoPatternEdit')
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleCanvasClick = (e: KonvaEventObject<MouseEvent>) => {
    if (e.target === stage.current) {
      transformer.current.nodes([])
      updateTextObjectProps()
      return
    }

    // do nothing if clicked NOT on our rectangles
    if (e.target.nodeType !== 'Shape') {
      return
    }
    // do we pressed shift or ctrl?
    const metaPressed = e.evt.shiftKey || e.evt.ctrlKey || e.evt.metaKey
    const isSelected = transformer.current.nodes().indexOf(e.target) >= 0

    if (!metaPressed && !isSelected) {
      transformer.current.nodes([e.target])
      overlayTextAreaOverElement(e.target)
    } else if (metaPressed && isSelected) {
      const nodes = transformer.current.nodes().slice() // use slice to have new copy of array
      nodes.splice(nodes.indexOf(e.target), 1)
      transformer.current.nodes(nodes)
    } else if (metaPressed && !isSelected) {
      // add the node into selection
      const nodes = transformer.current.nodes().concat([e.target])
      transformer.current.nodes(nodes)
    }
  }

  const addElementByType = (type: number) => {
    switch (type) {
      case 1:
        addElementWithData({
          type: 'text',
          text: 'Main Heading',
          fontSize: 30,
          fill: 'black',
          name: Math.random() + 'headingText',
          fontFamily: 'Calibri',
          x: 0,
          y: -200,
        })
        break
      case 2:
        addElementWithData({
          type: 'text',
          text: 'Sub Heading',
          fontSize: 20,
          fill: 'black',
          name: Math.random() + 'headingText',
          fontFamily: 'Calibri',
          x: 0,
          y: 0,
        })
        break
      case 3:
        addElementWithData({
          type: 'text',
          text: 'Enter your text here.',
          fontSize: 14,
          fill: 'black',
          name: Math.random() + 'headingText',
          fontFamily: 'Calibri',
          x: 0,
          y: 100,
        })
        break
      default:
        break
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const addElementWithData = (data: any) => {
    setTextElements([...textElements, data])
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const overlayTextAreaOverElement = (konvaElement: any) => {
    if (textArea.current && konvaElement) {
      const { x, y, rotation, scaleX = 1, scaleY = 1 } = konvaElement.attrs
      setCurrentEditingElement(konvaElement)
      const angle = (rotation / 180) * Math.PI
      const w = Math.round(konvaElement.width() * scaleX) + 7
      const h = Math.round(konvaElement.height() * scaleY)
      const offsetX = Math.cos(angle) * (w / 2 - (Math.tan(angle) * h) / 2) - w / 2
      const offsetY = Math.sin(angle) * (w / 2 - (Math.tan(angle / 2) * h) / 2)
      const fontSize = konvaElement.fontSize()
      textArea.current.style.display = 'block'
      textArea.current.style.left = `${x + offsetX}px`
      textArea.current.style.top = `${y + offsetY}px`
      textArea.current.style.width = w + 'px'
      textArea.current.style.height = h + 'px'
      textArea.current.style.fontSize = fontSize * scaleX + 'px'
      textArea.current.style.fontStyle = konvaElement.fontStyle()
      textArea.current.style.fontFamily = konvaElement.fontFamily()
      textArea.current.style.lineHeight = fontSize * scaleY + 'px'
      textArea.current.style.transform = 'rotate(' + konvaElement.attrs.rotation + 'deg)'
      textArea.current.value = konvaElement.text()
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onTextareaInput = () => {
    currentEditingElement.text(textArea.current.value)
    overlayTextAreaOverElement(currentEditingElement)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateTextObjectProps = (keepEditing?: boolean) => {
    if (!textContainer.current || !currentEditingElement) return
    textArea.current.style.display = 'none'
    const canvasWidth = textContainer.current.parentElement.clientWidth
    const canvasHeight = textContainer.current.parentElement.clientHeight
    const elementObject = textElements.find((elObj) => elObj.name === currentEditingElement.name())
    Object.assign(elementObject, currentEditingElement.attrs)
    elementObject.x -= canvasWidth / 2
    elementObject.y -= canvasHeight / 2
    setTextElements([...textElements])
    if (!keepEditing) setCurrentEditingElement(null)
  }

  const setMode = (mode: number) => {
    let newMode = mode
    Perform3DTask('updateMergeMode', mode)
    if (mode === appMode) {
      newMode = APPLICATION_MODES.NONE
      if (mode === 8 || mode == 9) return
      // console.log(`Mode : ${mode}, appmode : ${appMode}`)
      // this so that application mode is currently set based on current tab
      if (mode != 5 && mode != 6 && mode != 12) SetCurrentItem(selection, selectedTab)
    }
    setAppMode(newMode)
    console.log('Current mode : ', mode)
    CheckCurrentMode(mode)
  }

  const setAccentSlider = (scale: number) => {
    SetAccentScale(scale)
  }

  const toggleSliderVisibility = (visible: boolean) => {
    setSliderVisibility(visible)
    toggleSliderVisibilityCallBack(visible)
  }

  const updateSliderFromAccentScale = (scale: number) => {
    const minScale = 0.1
    const maxScale = 1

    const sliderValue = ((scale - minScale) / (maxScale - minScale)) * 100

    if (toolbarRef.current) {
      toolbarRef.current.changeAccentScaleSliderValue(sliderValue)
    }
    updateSliderFromAccentScaleCallBack(sliderValue)
  }

  const updateSliderFromAccentNumber = (number: number) => {
    // const minNumber = 1;
    // const maxNumber = 4;

    // const sliderValue = ((number - minNumber) / (maxNumber - minNumber)) * 100;

    if (toolbarRef.current) {
      toolbarRef.current.changeAccentNumberSliderValue(number)
    }
    updateSliderFromAccentNumberCallBack(number)
  }

  const updateSliderFromCameraHorizontalPosition = (number: number) => {
    if (toolbarRef.current) {
      toolbarRef.current.changeCameraHorizontalSliderValue(number)
    }
    updateSliderFromCameraHorizontalCallback(number)
  }

  const updateSliderFromAccentRotation = (number: number) => {
    if (toolbarRef.current) {
      toolbarRef.current.changeAccentRotationSliderValue(number)
    }
    updateSliderFromAccentRotationCallBack(number)
  }

  const updateSliderFromPrintTiling = (number: number) => {
    if (toolbarRef.current) {
      toolbarRef.current.changePrintTilingSliderValue(number)
    }
    updateSliderFromPrintTilingCallBack(number)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onTextUpdated = () => {
    updateTextObjectProps()
    updateCanvasData()
  }

  const onPressMerge = () => {
    if (appMode != APPLICATION_MODES.MERGE_SURFACES) {
      setMode(APPLICATION_MODES.MERGE_SURFACES)
      if (applyAll) changeStateApplyAll(false)
    } else {
      console.log('Perform task merge : ', appMode)
      Perform3DTask('updateMergeMode', appMode)
    }
  }
  const onPressSplit = () => {
    setMode(APPLICATION_MODES.SPLIT_SURFACES)
    if (applyAll) changeStateApplyAll(false)
  }
  const onPressRotate = () => {
    setMode(APPLICATION_MODES.ROTATE_TILE)
    if (applyAll) changeStateApplyAll(false)
  }

  const { dialog: WarningResetDialog, confirm: warningReset } = useConfirm({
    title: 'Reset Your Design?',
    message: 'Are you sure you want to reset this design? All unsaved changes will be lost.',
    destructive: true,
  })

  const onPressReset = async () => {
    const ans = await warningReset()
    if (ans) {
      console.log('Reset confirmed, proceeding with reset.')
      ResetTemplate()
      onResetTemplate()
    } else {
      console.log('Reset canceled.')
    }
  }

  const onPressHide = async () => {
    setMode(APPLICATION_MODES.HIDE_SURFACES)
    if (applyAll) changeStateApplyAll(false)
  }

  const onPressAll = () => {
    if (
      getApplicationMode() == 'print' ||
      getApplicationMode() == 'color' ||
      (getApplicationMode() === 'material' && getTopicType() === 'gallery')
    ) {
      let currentState = !getApplyToAllActive()
      console.log('Current apply all state : ', currentState)
      // setApplyToAllActive(currentState)
      // setApplyAll(currentState)
      changeStateApplyAll(currentState)
      if (appMode != APPLICATION_MODES.NONE) {
        setMode(APPLICATION_MODES.NONE)
      }
    }
    console.log(`Mode: ${getApplicationMode()} Apply to all : ${getApplyToAllActive()}`)
  }

  const changeStateApplyAll = (value: boolean) => {
    setApplyToAllActive(value)
    setApplyAll(value)
  }

  const onPressColorPick = () => {
    setMode(
      appMode === APPLICATION_MODES.PICKER_PICKED || appMode === APPLICATION_MODES.PICKER_PICKING
        ? APPLICATION_MODES.NONE
        : APPLICATION_MODES.PICKER_PICKING,
    )

    if (applyAll) changeStateApplyAll(false)
  }
  const onPressFullScreenMode = () => {
    setAppMode(
      appMode === APPLICATION_MODES.FULL_SCREEN
        ? APPLICATION_MODES.NONE
        : APPLICATION_MODES.FULL_SCREEN,
    )

    const modeName = Object.entries(APPLICATION_MODES).find(([_, value]) => value === appMode)?.[0]

    console.log('📱 Switching App Mode to:', modeName)

    onToggleFullScreenMode()
  }
  const onSliderAccentScaleChange = (value: number) => {
    setAccentSlider(value)
  }

  const onSliderAccentNumberChange = (value: number) => {
    SetAccentNumber(value)
  }

  const onSliderAccentRotationChange = (value: number) => {
    SetAccentRotation(value)
  }

  const onSliderPrintTilingChange = (value: number) => {
    SetPrintTiling(value)
  }

  const onCameraHorizontalPositionChange = (value: number) => {
    SetCameraHorizontalPosition(value)
  }

  const onCameraZoomInPressed = () => {
    CameraZoomInPressed()
  }

  const onCameraZoomOutPressed = () => {
    CameraZoomOutPressed()
  }

  /*
  There shows a red border when canvas is selected, we should add these two lines in style object 

  ```
  border: 'none',
  outline: 'none',
  ```

   */

  return (
    <Box position='relative' flex={1}>
      <canvas
        id='renderingCanvas'
        ref={canvas}
        style={{
          width: '100%',
          height: '100%',
          cursor: altKeyPressed ? 'grab' : 'auto',
        }}
      />
      {mode === 'edit' && (
        <PatternToolBar
          ref={toolbarRef}
          mode={appMode}
          bordered={showOverlay}
          debugMode={isInspectorShown}
          onPressBack={handleUndo}
          onPressForward={handleRedo}
          onPressMerge={onPressMerge}
          onPressSplit={onPressSplit}
          onPressRotate={onPressRotate}
          onPressAll={onPressAll}
          onPressColorPick={onPressColorPick}
          onPressGrid={toggleOverlay}
          onPressInspector={toggleInspector}
          onPressFullMode={onPressFullScreenMode}
          onSliderAccentScaleChange={onSliderAccentScaleChange}
          onSliderAccentNumberChange={onSliderAccentNumberChange}
          onSliderAccentRotationChange={onSliderAccentRotationChange}
          onSliderPrintTilingChange={onSliderPrintTilingChange}
          onCameraHorizontalPositionChange={onCameraHorizontalPositionChange}
          onCameraZoomInPressed={onCameraZoomInPressed}
          onCameraZoomOutPressed={onCameraZoomOutPressed}
          isSliderVisible={isSliderVisible}
          applyAll={applyAll}
          onPressReset={onPressReset}
          onPressHide={onPressHide}
        />
      )}
      {/* <SettingButtonWrapper>
        <IconButton sx={{ color: colors.purple }} onClick={onClickDone}>
          <DoneOutline></DoneOutline>
        </IconButton>
        <IconButton sx={{ color: colors.purple }} onClick={onTogglePreview}>
          {mode === 'edit' ? <PreviewOutlined></PreviewOutlined> : <EditOutlined></EditOutlined>}
        </IconButton>
        <IconButton sx={{ color: colors.purple }} onClick={onClickPrivacy}>
          <SVG src={SVGS.FilterPrivacyPrivate} />
        </IconButton>
      </SettingButtonWrapper> */}
      {/* Add WarningResetDialog to the component */}
      <WarningResetDialog />
    </Box>
  )
})

export default PatternPreview
